import { Collapse, SxProps, Typography } from "@mui/material";
import { useMemo } from "react";
import { Link, useGetMany, useGetOne } from "react-admin";
import { AddStudentToTheoryLessonButton } from "../../buttons/AddStudentToTheoryLessonButton";
import {
  ASFCourseSession,
  ASFCourseSessionAttendance,
  isTheoryLesson,
  TheoryLesson,
  TheoryLessonStudentAttendance,
} from "../../model/autovioCalendarEvents";
import { StudentAvatar } from "../../misc/StudentAvatar";
import { StudentDisplayName } from "../../misc/StudentDisplayName";
import { TransitionGroup } from "react-transition-group";
import { Student } from "../../providers/studentsProvider";
import { grants } from "../../backoffice.access_control";
import { DateTime } from "luxon";
import { Column } from "../../misc/Column";
import { Row } from "../../misc/Row";
import { RemoveStudentFromTheoryLessonButton } from "../../buttons/RemoveStudentFromTheoryLessonButton";
import { SetStudentAttendanceButtons } from "../../buttons/SetStudentAttendanceButtons";
import { CourseEvent } from "../../providers/coursesProvider";

export function AttendeesList(props: { event: TheoryLesson | ASFCourseSession; sx?: SxProps }) {
  // We fetch the given event here via useGetOne so that we rerender whenever the event changes ...
  const { data } = useGetOne<CourseEvent>("calendarEvents", { id: props.event.id });
  const event = data ?? props.event;
  const attendanceByStudentId: Record<string, TheoryLessonStudentAttendance | ASFCourseSessionAttendance> =
    event.students || event.participants;
  const {
    data: students,
    isLoading: studentsIsLoading,
    error: studentsError,
  } = useGetMany<Student>("students", {
    ids: Object.keys(attendanceByStudentId),
    meta: { drivingSchoolId: event.drivingSchoolId },
  });
  const attendanceSortedByName: Array<[string, TheoryLessonStudentAttendance | ASFCourseSessionAttendance]> =
    useMemo(() => {
      const entries = Object.entries(attendanceByStudentId);
      if (!students || studentsIsLoading || studentsError) {
        return entries;
      }
      return entries.sort(([uid1], [uid2]) => {
        const student1 = students.find((student) => student.id === uid1);
        const student2 = students.find((student) => student.id === uid2);
        if (!student1 || !student2) return 0;
        return student1.name.localeCompare(student2.name);
      });
    }, [attendanceByStudentId, students]);
  const canAddStudents = isTheoryLesson(event) && grants.has("addStudentToTheoryLesson");
  const canRemoveStudents =
    isTheoryLesson(event) && grants.has("removeStudentFromTheoryLesson") && DateTime.now() < event.start;

  const canDocumentStudentsAttendance = isTheoryLesson(event) && DateTime.now() >= event.start;

  const registeredStudents =
    event.type === "ASFCourseSession"
      ? attendanceSortedByName
      : attendanceSortedByName.filter(([_, attendance]) => attendance.rsvp === "accepted");
  const deregisteredStudents = attendanceSortedByName.filter(([_, attendance]) => attendance.rsvp === "rejected");
  return (
    <Column spacing="4px" sx={props.sx}>
      <Row sx={{ justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" fontWeight="bold" color="text.primary">
          Angemeldete Fahrschüler
        </Typography>
        {canDocumentStudentsAttendance && (
          <Typography variant="body2" sx={{ color: "text.disabled" }}>
            Anwesenheit
          </Typography>
        )}
      </Row>
      <TransitionGroup component="div" style={{ display: "flex", flexDirection: "column" }}>
        {registeredStudents.map(([uid, attendance], index) => {
          return (
            <Collapse key={uid} unmountOnExit>
              <Row
                sx={{
                  height: "40px",
                  borderBottom: index === registeredStudents.length - 1 ? 0 : 1,
                  borderColor: "divider",
                }}
              >
                <Link to={`/students/${uid}`} sx={{ display: "flex", alignItems: "center" }}>
                  <StudentAvatar studentId={uid} size="20px" />
                  <StudentDisplayName sx={{ ml: ".5em" }} studentId={uid} />
                </Link>
                <div style={{ flex: 1 }} />
                {canRemoveStudents && <RemoveStudentFromTheoryLessonButton theoryLesson={event} studentId={uid} />}
                {canDocumentStudentsAttendance && attendance && (
                  <SetStudentAttendanceButtons
                    studentId={uid}
                    studentAttendance={attendance as TheoryLessonStudentAttendance}
                  />
                )}
              </Row>
            </Collapse>
          );
        })}
      </TransitionGroup>
      {canAddStudents && <AddStudentToTheoryLessonButton sx={{ mt: "10px" }} theoryLesson={event} />}
      {deregisteredStudents.length > 0 && (
        <>
          <Typography variant="body2" fontWeight="bold">
            Abgemeldete Fahrschüler
          </Typography>
          <Column>
            {deregisteredStudents.map(([uid], index) => (
              <Row
                key={uid}
                sx={{
                  height: "40px",
                  borderBottom: index === deregisteredStudents.length - 1 ? 0 : 1,
                  borderColor: "divider",
                }}
              >
                <Link to={`/students/${uid}`} sx={{ display: "flex", alignItems: "center" }}>
                  <StudentAvatar studentId={uid} size="20px" />
                  <StudentDisplayName sx={{ ml: ".5em" }} studentId={uid} />
                </Link>
              </Row>
            ))}
          </Column>
        </>
      )}
    </Column>
  );
}
